import { createPortal } from 'react-dom';
import { useCallback } from 'react';
import { useFormikContext } from 'formik';
import { useParams } from 'react-router-dom';
import type { FieldArrayRenderProps } from 'formik';

import { getFragmentData } from '@e-socna/data-layer';
import type { Id } from '@e-socna/data-layer';

import { Addresses } from '../../../../../components/Addresses';
import { RightDrawer } from '../../../../../components/RightDrawer';
import { useActionBlocker } from '../../../../../hooks/useActionBlocker.hook';

import { FormVariant } from '../../../../enums/variant.enum';
import { AddressForm } from '../../../../Address';
import { AddressForm_Address_Fragment } from '../../../../Address/fragments/address.fragment';
import type { AddressForm_Address } from '../../../../Address/fragments/address.fragment';

import type { ThirdPartyForm_ThirdParty } from '../../../fragments/third-party.fragment';

import { DeleteAddressForm } from '../Delete';
import type { Props } from '../types/props.type';

import { useAddressesReducer } from './useReducer.hook';
import { useFindAddressIndexById } from './useFindIndexById.hook';
import { useCreateThirdPartyAddressSubmit } from './useCreateThirdPartyAddressSubmit.hook';
import { useUpdateThirdPartyAddressSubmit } from './useUpdateThirdPartyAddressSubmit.hook';
import { useRemoveThirdPartyAddressSubmit } from './useRemoveThirdPartyAddressSubmit.hook';

export function useAddressesFieldArrayRenderer({
  paths,
  useDirectMutation,
  config,
}: Readonly<Props>) {
  const { values } = useFormikContext<ThirdPartyForm_ThirdParty>();

  const { addressId: addressIdParam } = useParams();

  const thirdPartyInfo = {
    _id: values._id,
    isInternal: values.isInternal,
    kind: values.kind,
    statusId: values.statusId,
  };

  const {
    state,
    handleClearState,
    handleStartRemove,
    handleStartUpdate,
    handleStartCreate,
    handleCreateSuccess,
    handleUpdateSuccess,
    handleRemoveSuccess,
  } = useAddressesReducer({
    paths,
    thirdPartyInfo,
  });

  const findAddressIndexById = useFindAddressIndexById();

  const rawInitialValues =
    paths && addressIdParam
      ? values.addresses[findAddressIndexById(addressIdParam as Id)]
      : state.idToUpdate
        ? values.addresses[findAddressIndexById(state.idToUpdate)]
        : undefined;

  const initialValues = getFragmentData(
    AddressForm_Address_Fragment,
    rawInitialValues,
  );

  const { handleSubmitFactory: handleCreateSubmitFactory } =
    useCreateThirdPartyAddressSubmit({
      handleCreateSuccess,
      thirdPartyId: thirdPartyInfo._id,
      useDirectMutation,
    });

  const { handleSubmitFactory: handleUpdateSubmitFactory } =
    useUpdateThirdPartyAddressSubmit({
      addressId: state.idToUpdate,
      handleUpdateSuccess,
      thirdPartyId: thirdPartyInfo._id,
      useDirectMutation,
    });

  const { handleSubmitFactory: handleRemoveSubmitFactory } =
    useRemoveThirdPartyAddressSubmit({
      addressId: state.idToRemove,
      handleRemoveSuccess,
      thirdPartyId: thirdPartyInfo._id,
      useDirectMutation,
    });

  const [blockableClearStateHandler, setMustBlockAction] =
    useActionBlocker(handleClearState);

  const fieldArrayRender = useCallback(
    (fieldArray: Readonly<FieldArrayRenderProps>) => {
      const handleCreateSubmit = handleCreateSubmitFactory({
        createInForm: (value: Readonly<AddressForm_Address>) => {
          fieldArray.push(value);
        },
      });

      const handleUpdateSubmit = handleUpdateSubmitFactory({
        updateInForm: (index: number, value: Readonly<AddressForm_Address>) => {
          fieldArray.replace(index, value);
        },
      });

      const handleRemoveSubmit = handleRemoveSubmitFactory({
        removeInForm: (index: number) => {
          fieldArray.remove(index);
        },
      });

      return (
        <>
          <Addresses
            actionsEnabled
            addresses={values.addresses}
            onStartCreate={handleStartCreate}
            onStartRemove={handleStartRemove}
            onStartUpdate={handleStartUpdate}
          />
          {/* NB : We don't want to nest forms in HTML, so we extract these in a portal */}
          {createPortal(
            <DeleteAddressForm
              askMemoBeforeSubmit={useDirectMutation}
              isDialogOpen={state.isRemoveDialogOpen}
              onCloseDialog={handleClearState}
              onSubmit={handleRemoveSubmit}
              useDirectMutation={useDirectMutation}
            />,
            document.body,
          )}
          <RightDrawer
            isOpen={state.isCreateDrawerOpen || state.isUpdateDrawerOpen}
            onDrawerClose={blockableClearStateHandler}
          >
            <AddressForm
              askMemoBeforeSubmit={useDirectMutation}
              config={config}
              formVariant={
                state.isUpdateDrawerOpen
                  ? FormVariant.Modification
                  : FormVariant.Creation
              }
              initialValues={initialValues}
              onCancel={blockableClearStateHandler}
              onSubmit={
                state.isUpdateDrawerOpen
                  ? handleUpdateSubmit
                  : handleCreateSubmit
              }
              setMustBlockAction={!paths ? setMustBlockAction : undefined}
              showCancelButton
              title={
                state.isUpdateDrawerOpen
                  ? "Formulaire de modification de l'Adresse"
                  : "Formulaire de création d'une Adresse"
              }
            />
          </RightDrawer>
        </>
      );
    },
    [
      blockableClearStateHandler,
      config,
      handleClearState,
      handleCreateSubmitFactory,
      handleRemoveSubmitFactory,
      handleStartCreate,
      handleStartRemove,
      handleStartUpdate,
      handleUpdateSubmitFactory,
      initialValues,
      paths,
      setMustBlockAction,
      state,
      useDirectMutation,
      values.addresses,
    ],
  );

  return { fieldArrayRender };
}
